import React, { useEffect, useState } from "react";
import ResearchLayout, { useReveal } from "../ResearchLayout";
import config from "../../../config";
import makeRequest from "../../../makeRequest";
import { ROLE_PALETTE } from "../researchApi";

const TIMELINE_ICONS = ["fa-calendar-check", "fa-podium", "fa-handshake", "fa-people-group", "fa-microchip", "fa-brain", "fa-globe"];

export default function Conferences() {
  useReveal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await makeRequest({ route: "/website/research-conference/public", method: "get" });
        if (mounted) setItems(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        console.error("Failed to load conferences:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <ResearchLayout>
      <section
        style={{
          padding: "100px 0 60px",
          background: "linear-gradient(135deg, #0d3b6e 0%, #076cec 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="rs-blob" style={{ width: 400, height: 400, background: "#04b3bf", top: "-150px", right: "-100px" }} />
        <div
          className="rs-blob"
          style={{ width: 350, height: 350, background: "#7c3aed", bottom: "-100px", left: "-100px", animationDelay: "2s" }}
        />
        <div className="rs-container rs-text-center" style={{ position: "relative", zIndex: 2 }}>
          <span className="rs-section-eyebrow" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(10px)" }}>
            Knowledge Sharing
          </span>
          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", fontWeight: 800, margin: "12px 0 16px" }}>Conferences &amp; Meetings</h1>
          <p style={{ fontSize: "1.05rem", maxWidth: 720, margin: "0 auto", opacity: 0.85, lineHeight: 1.7 }}>
            From international training programs to national summits — our team actively contributes to advancing clinical research in Pakistan
            and beyond.
          </p>
        </div>
      </section>

      <section className="rs-section" style={{ paddingTop: 60 }}>
        <div className="rs-container">
          {loading ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: "#6b7280" }}>
              <i className="fa-regular fa-spinner fa-spin" style={{ fontSize: "1.8rem", color: "var(--rs-primary)", marginBottom: 12 }}></i>
              <div>Loading conferences…</div>
            </div>
          ) : items.length === 0 ? (
            <div
              style={{
                padding: "50px 30px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
                borderRadius: 18,
                border: "1px dashed rgba(7, 108, 236,0.2)",
              }}
            >
              <h3 style={{ margin: "0 0 8px", color: "var(--rs-dark)", fontWeight: 800 }}>No events yet</h3>
              <p style={{ color: "#6b7280", margin: 0 }}>Add Research Conferences from the admin panel to populate this timeline.</p>
            </div>
          ) : (
            <div style={{ position: "relative", paddingLeft: 40 }} className="rs-timeline">
              <div
                style={{
                  position: "absolute",
                  left: 14,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: "linear-gradient(180deg, var(--rs-primary), var(--rs-secondary))",
                  borderRadius: 3,
                }}
              />

              {items.map((e, i) => (
                <ConferenceCard key={e.id} e={e} index={i} />
              ))}
            </div>
          )}

          <style>{`
            @media (max-width: 600px) {
              .rs-timeline { padding-left: 28px !important; }
            }
          `}</style>
        </div>
      </section>
    </ResearchLayout>
  );
}

function ConferenceCard({ e, index }) {
  const [imgError, setImgError] = useState(false);
  const color = ROLE_PALETTE[index % ROLE_PALETTE.length];
  const icon = TIMELINE_ICONS[index % TIMELINE_ICONS.length];
  const showImage = e.hasImage && !imgError;
  const imgUrl = `${config.api.baseUrl}/website/research-conference/${e.id}/image`;

  /* Description bullets: split on newlines or • characters; keeps display flexible. */
  const bullets = String(e.description || "")
    .split(/\r?\n|•/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className={`rs-reveal delay-${(index % 4) + 1}`} style={{ position: "relative", marginBottom: 36 }}>
      <div
        style={{
          position: "absolute",
          left: -33,
          top: 22,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#fff",
          border: "4px solid var(--rs-primary)",
          boxShadow: "0 0 0 6px rgba(7, 108, 236,0.1)",
        }}
      />
      <div
        style={{
          background: "#fff",
          borderRadius: 18,
          overflow: "hidden",
          border: "1px solid rgba(7, 108, 236,0.08)",
          transition: "all 0.3s",
        }}
        onMouseEnter={(ev) => {
          ev.currentTarget.style.transform = "translateX(8px)";
          ev.currentTarget.style.boxShadow = "0 16px 40px rgba(7, 108, 236,0.12)";
        }}
        onMouseLeave={(ev) => {
          ev.currentTarget.style.transform = "translateX(0)";
          ev.currentTarget.style.boxShadow = "none";
        }}
      >
        <div
          style={{
            background: color,
            color: "#fff",
            padding: "14px 22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.05rem",
              }}
            >
              <i className={`fa-regular ${icon}`}></i>
            </div>
            {e.tag && (
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "5px 11px",
                  borderRadius: 50,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                {e.tag}
              </span>
            )}
          </div>
          {e.eventDate && (
            <span style={{ fontSize: "0.82rem", fontWeight: 700, opacity: 0.95 }}>
              <i className="fa-regular fa-calendar" style={{ marginRight: 6 }}></i>
              {e.eventDate}
            </span>
          )}
        </div>

        {showImage && (
          <div style={{ width: "100%", maxHeight: 280, overflow: "hidden", background: "#f8fafc" }}>
            <img
              src={imgUrl}
              alt={e.title}
              onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        )}

        <div style={{ padding: "22px 26px" }}>
          <h3 style={{ margin: "0 0 6px", fontSize: "1.2rem", fontWeight: 800, color: "var(--rs-dark)" }}>{e.title}</h3>
          {e.location && (
            <div
              style={{
                fontSize: "0.82rem",
                color: "#6b7280",
                marginBottom: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="fa-regular fa-location-dot"></i>
              {e.location}
            </div>
          )}
          {bullets.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 20, color: "#374151", lineHeight: 1.7 }}>
              {bullets.map((b, j) => (
                <li key={j} style={{ marginBottom: 6 }}>
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
